import { useState, useCallback } from "react";
import { createCheckoutSession } from "../services/api";
import { useNavigate } from "react-router-dom";
import { isCreditTopUpPlan } from "../const/creditTopUpPacks";

/**
 * Custom hook for payment operations
 * Provides payment initiation with loading and error states
 */
export const usePayment = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const navigate = useNavigate();

	const initiatePayment = useCallback(
		async (payload, options = {}) => {
			setLoading(true);
			setError(null);

			try {
				const response = await createCheckoutSession(payload);

				if (response?.data?.url) {
					// Redirect to Stripe checkout
					if (options.redirect !== false) {
						window.location.href = response.data.url;
					}
					return { success: true, url: response.data.url, sessionId: response.data.id };
				} else if (response?.data?.success === false && response?.data?.error) {
					throw new Error(response.data.error);
				} else if (response?.data?.success && response.data.orderId) {
					// Credit-based report purchase (not top-ups)
					if (isCreditTopUpPlan(payload.planType)) {
						throw new Error("Credit top-up must be completed through Stripe checkout.");
					}
					if (options.redirect !== false) {
						navigate(`/report-generation?session_id=${response.data.orderId}`);
					}
					return { success: true, orderId: response.data.orderId };
				} else {
					throw new Error("Invalid response from payment server");
				}
			} catch (err) {
				const errorCode = err?.response?.data?.code;
				const errorMessage =
					err?.response?.data?.error ||
					err?.message ||
					"Payment initiation failed. Please try again.";
				setError(errorMessage);
				console.error("Payment initiation failed:", err);
				const wrappedError = new Error(errorMessage);
				wrappedError.code = errorCode;
				throw wrappedError;
			} finally {
				setLoading(false);
			}
		},
		[navigate]
	);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	return {
		initiatePayment,
		loading,
		error,
		clearError,
	};
};

